'use client';

import { useDotEnv } from '@repo/dot-env/client';
import { getAddressForCoordinates, type GoogleLocationResult, type NormalizedAddress, type NormalizedPlace } from '@repo/geolocation';
import { z } from '@repo/schema';
import type { CompanyLocation } from '@repo/schema/location';
import { Button, Separator } from '@repo/ui/components';
import { MapPin } from '@repo/ui/icons';
import { TEST_IDS } from '@repo/sdk/constants';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { withForm } from '../../lib/app-form-hook.tsx';
import { LocationAddressFields } from './location-address-fields.tsx';
import { LocationDetailsFields } from './location-details-fields.tsx';
import { LocationGeolocationFields } from './location-geolocation-fields.tsx';
import { LocationSearchStep, type PlaceDetails } from './location-search-step.tsx';
const StringCoordinateSchema = z.string().trim().min(1, 'Coordinate is required').transform(v => Number(v)).refine(v => !Number.isNaN(v) && Number.isFinite(v), 'Invalid coordinate');
const trimmedStringToNullable = (max: number) => z.string().trim().max(max).transform(value => value || null);
const nullableTrimmedString = (max: number) => z.preprocess(value => value === null ? '' : value, z.string().trim().max(max)).transform(value => value || null) as unknown as ReturnType<typeof trimmedStringToNullable>;
export const LocationInputSchema = z.object({
  address: z.object({
    address: z.string().trim().max(255),
    address_second_line: nullableTrimmedString(255),
    city: z.string().trim().min(1).max(255),
    country: z.string().trim().toUpperCase().length(2),
    lat: StringCoordinateSchema,
    lng: StringCoordinateSchema,
    place_id: z.string().nullable(),
    place_name: z.string().nullable(),
    postcode: nullableTrimmedString(255)
  }),
  hide_geolocation: z.boolean(),
  instructions: z.string().max(160, 'Text must not exceed 160 characters.').nullable(),
  name: z.string().trim().min(1).max(255)
});
export type CompanyLocationFormValues = {
  address: {
    address: string;
    address_second_line: string;
    city: string;
    country: string;
    lat: string;
    lng: string;
    place_id: null | string;
    place_name: null | string;
    postcode: string;
  };
  hide_geolocation: boolean;
  instructions: string;
  name: string;
};
export type NormalizedCompanyLocationPayload = z.output<typeof LocationInputSchema>;
type NullableCompanyLocationFormValues = Omit<CompanyLocationFormValues, 'address' | 'instructions'> & {
  address: Omit<CompanyLocationFormValues['address'], 'address_second_line' | 'postcode'> & {
    address_second_line: null | string;
    postcode: null | string;
  };
  instructions: null | string;
};
export const companyLocationDefaultValues: CompanyLocationFormValues = {
  address: {
    address: '',
    address_second_line: '',
    city: '',
    country: '',
    lat: '',
    lng: '',
    place_id: null,
    place_name: null,
    postcode: ''
  },
  hide_geolocation: true,
  instructions: '',
  name: ''
};
export const normalizeCompanyLocationPayload = (values: CompanyLocationFormValues | NullableCompanyLocationFormValues): NormalizedCompanyLocationPayload => {
  return LocationInputSchema.parse(values);
};
export type CompanyLocationPlaceInput = {
  /** Street line from the normalized Google address. */
  address?: null | string;
  city?: null | string;
  country?: null | string;
  country_code?: null | string;
  lat?: null | string;
  lng?: null | string;
  name?: null | string;
  place_id?: null | string;
  place_name?: null | string;
  postcode?: null | string;
};
export const applyPlaceToCompanyLocationValues = (place: CompanyLocationPlaceInput): Partial<CompanyLocationFormValues> => {
  const countryCode = place.country_code?.trim();
  const country = countryCode?.length === 2 ? countryCode : place.country?.trim().length === 2 ? place.country.trim() : '';
  const addressLine1 = place.address?.trim() || place.place_name || '';
  const name = place.name?.trim() || place.place_name || '';
  return {
    address: {
      address: addressLine1,
      address_second_line: '',
      city: place.city ?? '',
      country,
      lat: place.lat ?? '',
      lng: place.lng ?? '',
      place_id: place.place_id ?? null,
      place_name: place.place_name ?? null,
      postcode: place.postcode ?? ''
    },
    name
  };
};
export const mapCompanyLocationToFormValues = (location: CompanyLocation): CompanyLocationFormValues => {
  const address = location.address ?? null;
  const lat = address?.lat == null ? '' : typeof address.lat === 'number' ? String(address.lat) : address.lat;
  const lng = address?.lng == null ? '' : typeof address.lng === 'number' ? String(address.lng) : address.lng;
  const countryCode = address?.country_code?.trim();
  const country = countryCode?.length === 2 ? countryCode : address?.country?.trim().length === 2 ? address.country.trim() : '';
  return {
    address: {
      address: address?.address ?? '',
      address_second_line: address?.address_second_line ?? '',
      city: address?.city ?? '',
      country,
      lat,
      lng,
      place_id: address?.place_id ?? null,
      place_name: address?.place_name ?? null,
      postcode: address?.postcode ?? ''
    },
    hide_geolocation: location.hide_geolocation ?? false,
    instructions: location.instructions ?? '',
    name: location.name
  };
};
export const applyPinOverrideToCompanyLocationValues = <T extends Pick<CompanyLocationFormValues, 'address'>,>(values: T, coordinates: {
  lat: number;
  lng: number;
}): T => ({
  ...values,
  address: {
    ...values.address,
    lat: String(coordinates.lat),
    lng: String(coordinates.lng),
    place_id: null,
    place_name: null
  }
});
export const buildCompanyLocationValuesFromGooglePlace = ({
  address
}: NormalizedPlace, place: Pick<GoogleLocationResult, 'place_id' | 'place_name'>): Partial<CompanyLocationFormValues> => applyPlaceToCompanyLocationValues({
  address: address.addressLine,
  city: address.city,
  country_code: address.countryCode,
  lat: String(address.lat),
  lng: String(address.lng),
  name: address.name,
  place_id: place.place_id,
  place_name: place.place_name,
  postcode: address.postcode
});
export const buildCompanyLocationValuesFromCoordinates = (coordinates: {
  lat: number;
  lng: number;
}, address?: NormalizedAddress): Partial<CompanyLocationFormValues> => ({
  address: {
    ...companyLocationDefaultValues.address,
    address: address?.addressLine ?? '',
    city: address?.city ?? '',
    country: address?.countryCode ?? '',
    lat: String(coordinates.lat),
    lng: String(coordinates.lng),
    place_id: null,
    place_name: null,
    postcode: address?.postcode ?? ''
  },
  name: ''
});
export const createCompanyLocationFormSchema = (options?: {
  requireAddressLine1?: boolean;
}) => {
  return z.object({
    address: z.object({
      address: options?.requireAddressLine1 ? z.string().trim().min(1, 'Address is required').max(255) : z.string().trim().max(255),
      address_second_line: nullableTrimmedString(255),
      city: z.string().trim().min(1, 'City is required').max(255),
      country: z.string().trim().length(2, 'Country is required'),
      lat: z.string().trim().min(1, 'Latitude is required').refine(v => Number.isFinite(Number(v)), 'Must be a valid number'),
      lng: z.string().trim().min(1, 'Longitude is required').refine(v => Number.isFinite(Number(v)), 'Must be a valid number'),
      place_id: z.string().nullable(),
      place_name: z.string().nullable(),
      postcode: nullableTrimmedString(255)
    }),
    hide_geolocation: z.boolean(),
    instructions: z.string().max(160, 'Text must not exceed 160 characters.'),
    name: z.string().trim().min(1, 'Name is required').max(255)
  });
};
type CompanyLocationFormRenderProps = {
  cancelLabel?: string;
  disabled?: boolean;
  errorContent?: ReactNode;
  mode: 'create' | 'edit';
  onCancel?: () => void;
  prefilled?: boolean;
  search?: {
    initialQuery?: string;
  };
  showActions?: boolean;
  submitIcon?: ReactNode;
  submitLabel?: string;
  submitPending?: boolean;
};
export const CompanyLocationForm = withForm({
  defaultValues: companyLocationDefaultValues,
  // SAFETY: `props` only carries the render-props type. withForm spreads it under the
  // caller's props, the empty object adds no keys, and the component type makes every
  // caller pass the required `mode`.
  props: {} as CompanyLocationFormRenderProps,
  render: function Render({
    cancelLabel = 'Cancel',
    disabled = false,
    errorContent,
    form,
    mode,
    onCancel,
    prefilled = false,
    search,
    showActions = true,
    submitIcon = <MapPin className="size-4" />,
    submitLabel = mode === 'create' ? 'Add location' : 'Update location',
    submitPending = false
  }) {
    const {
      GEOLOCATION_SERVICE_URL
    } = useDotEnv();
    const [isRevealed, setIsRevealed] = useState(mode === 'edit' || !search || prefilled);
    const showSearch = mode === 'edit' || Boolean(search);
    const applyValuesToForm = (values: Partial<CompanyLocationFormValues>) => {
      if (values.name !== undefined) {
        form.setFieldValue('name', values.name);
      }
      if (values.address !== undefined) {
        const currentAddress = form.getFieldValue('address');
        form.setFieldValue('address', {
          ...currentAddress,
          ...values.address
        });
      }
      setIsRevealed(true);
    };
    const handlePlaceSelected = (place: PlaceDetails) => {
      applyValuesToForm(applyPlaceToCompanyLocationValues(place));
    };
    const handleCoordinatesSelected = (coordinates: {
      lat: number;
      lng: number;
    }) => {
      applyValuesToForm(buildCompanyLocationValuesFromCoordinates(coordinates));
      const enrichAddress = async () => {
        const result = await getAddressForCoordinates(coordinates, GEOLOCATION_SERVICE_URL);
        if (result.status !== 'found') return;
        const resolvedAddress = result.value.address;
        const currentAddress = form.getFieldValue('address');
        if (currentAddress.lat !== String(coordinates.lat) || currentAddress.lng !== String(coordinates.lng) || currentAddress.place_id !== null) {
          return;
        }
        form.setFieldValue('address', {
          ...currentAddress,
          address: currentAddress.address || resolvedAddress.addressLine,
          city: currentAddress.city || resolvedAddress.city,
          country: currentAddress.country || resolvedAddress.countryCode,
          postcode: currentAddress.postcode || resolvedAddress.postcode
        });
      };
      void enrichAddress();
    };
    const handleManualEntry = (query?: string) => {
      if (query?.trim()) {
        form.setFieldValue('name', query.trim());
      }
      setIsRevealed(true);
    };
    return <form.AppForm>
        <form className="flex flex-col gap-8" onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}>
          {showSearch && <>
              <LocationSearchStep initialQuery={search?.initialQuery} onCoordinatesSelected={handleCoordinatesSelected} onManualEntry={handleManualEntry} onPlaceSelected={handlePlaceSelected} />
              {isRevealed && <Separator />}
            </>}
          {isRevealed && <>
              <LocationDetailsFields disabled={disabled} fields={{
            instructions: 'instructions',
            name: 'name'
          }} form={form} />
              <Separator />
              <LocationAddressFields disabled={disabled} fields={{
            address: 'address.address',
            address_second_line: 'address.address_second_line',
            city: 'address.city',
            country: 'address.country',
            place_id: 'address.place_id',
            place_name: 'address.place_name',
            postcode: 'address.postcode'
          }} form={form} />
              <Separator />
              <LocationGeolocationFields disabled={disabled} fields={{
            hide_geolocation: 'hide_geolocation',
            lat: 'address.lat',
            lng: 'address.lng'
          }} form={form} onCoordinatesSelected={coordinates => {
            form.setFieldValue('address', applyPinOverrideToCompanyLocationValues({
              address: form.getFieldValue('address'),
              instructions: form.getFieldValue('instructions'),
              name: form.getFieldValue('name')
            }, coordinates).address);
          }} />
              {errorContent}
              {showActions && <div className="flex justify-between gap-4">
                  {onCancel && <Button data-testid={TEST_IDS.companyLocation.form.cancelButton} disabled={disabled} onClick={onCancel} type="button" variant="outline">
                      {cancelLabel}
                    </Button>}
                  <Button data-testid={TEST_IDS.companyLocation.form.submitButton} disabled={disabled || form.state.isSubmitting || submitPending} type="submit" variant="default">
                    {submitIcon}
                    {submitLabel}
                  </Button>
                </div>}
            </>}
        </form>
      </form.AppForm>;
  }
});